import { createFileRoute } from '@tanstack/react-router'
import { Landmark, PlayCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent } from '@/shared/ui/card'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/configuracoes/cobranca')({
  component: CobrancaPage,
})

const STEPS = ['institutions', 'profiles', 'methods', 'portal'] as const
const BANKS = ['pjbank', 'inter', 'itau', 'cora'] as const
const HELP_ITEMS = ['setup', 'firstRun', 'automate', 'whatsapp'] as const

/**
 * Cobrança (integração bancária: PJBank/Inter/Itaú/Cora). O backend NÃO expõe
 * endpoints de integração de cobrança — a tela replica o layout do print, mas
 * cada "Configurar" é DESABILITADO "Em breve" e os passos/ajuda são presentacionais.
 * O banner de topo (nav-config: coming-soon) explica a causa.
 */
function CobrancaPage() {
  const { t } = useTranslation(['settings', 'nav', 'common'])
  const comingSoon = t('common:comingSoon.title')

  return (
    <>
      <PageHeader title={t('nav:settings.billing')} />

      <div className="grid gap-6 p-6 lg:grid-cols-[16rem_1fr]">
        {/* Coluna esquerda: passos numerados (presentacionais) + ajuda. */}
        <aside className="flex flex-col gap-6">
          <ol className="flex flex-col gap-1">
            {STEPS.map((step, i) => {
              const ativo = i === 0
              return (
                <li key={step}>
                  <span
                    className={
                      ativo
                        ? 'text-action flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium'
                        : 'text-ink-subtle flex cursor-not-allowed items-center gap-2 rounded-md px-3 py-2 text-sm'
                    }
                    aria-current={ativo ? 'step' : undefined}
                    aria-disabled={ativo ? undefined : true}
                    title={ativo ? undefined : comingSoon}
                  >
                    <span className="tabular-nums">{i + 1}.</span>
                    {t(`settings:billing.steps.${step}`)}
                    {!ativo && (
                      <Badge tone="soon" className="ml-auto">
                        {t('common:comingSoon.badge')}
                      </Badge>
                    )}
                  </span>
                </li>
              )
            })}
          </ol>

          <Card>
            <CardContent className="flex flex-col gap-3 pt-4">
              <h2 className="text-ink text-sm font-semibold">{t('settings:billing.help.title')}</h2>
              <div className="bg-surface-2 text-ink-subtle grid aspect-video place-items-center rounded-md">
                <PlayCircle className="size-10" aria-hidden />
              </div>
              <ul className="flex flex-col gap-2">
                {HELP_ITEMS.map((item) => (
                  <li key={item}>
                    <span
                      className="text-ink-subtle inline-flex cursor-not-allowed items-center gap-2 text-sm"
                      aria-disabled
                      title={comingSoon}
                    >
                      {t(`settings:billing.help.items.${item}`)}
                      <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </aside>

        {/* Coluna principal: instituições financeiras. */}
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-ink text-lg font-semibold">
              {t('settings:billing.institutions.title')}
            </h2>
            <p className="text-ink-muted text-sm">{t('settings:billing.institutions.subtitle')}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {BANKS.map((bank) => (
              <Card key={bank} className="flex flex-col">
                <CardContent className="flex flex-1 flex-col gap-3 pt-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-action-bg text-action grid size-10 shrink-0 place-items-center rounded-md">
                      <Landmark className="size-5" aria-hidden />
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <span className="text-ink flex items-center gap-2 text-sm font-semibold">
                        {t(`settings:billing.banks.${bank}.name`)}
                        {bank === 'pjbank' && (
                          <Badge tone="positive">{t('settings:billing.recommended')}</Badge>
                        )}
                      </span>
                      <span className="text-ink-muted text-xs">
                        {t('settings:billing.accountType')}
                      </span>
                    </div>
                  </div>

                  <p className="text-ink-muted flex-1 text-sm">
                    {t(`settings:billing.banks.${bank}.desc`)}
                  </p>

                  {/* Sem endpoint de integração → "Configurar" desabilitado. */}
                  <div className="flex items-center gap-2">
                    <Button variant="primary" disabled aria-disabled title={comingSoon}>
                      {t('settings:billing.configure')}
                    </Button>
                    <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </>
  )
}
